import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Main } from '@/components/layout/main';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { DataTable } from '@/components/table';
import { ColumnDef } from '@tanstack/react-table';
import { DataTableColumnHeader } from '@/components/data-table-column-header';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { superAdminService } from '@/api/super-admin';
import type { AuditLog, AuditLogsParams } from '@/types/super-admin';
import { handleServerError } from '@/utils/handle-server-error';
import { Eye, ScrollText } from 'lucide-react';
import { PageHeader } from '@/components/layout/page-header';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { format } from 'date-fns';
import { timestamp_to_date } from '@/utils/time';

export default function AuditLogsManagement() {
  const { t } = useTranslation();
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [pagesCnt, setPagesCnt] = useState(1);
  const [selectedLog, setSelectedLog] = useState<AuditLog | null>(null);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);

  const [params, setParams] = useState<AuditLogsParams>({
    page: 0,
    page_size: 10,
    ordering: '-timestamp',
    action: null,
    model_name: null,
    user: null,
    user_name: null,
    timestamp_after: null,
    timestamp_before: null,
  });

  // Additional filter states
  const [searchUser, setSearchUser] = useState('');
  const [filterAction, setFilterAction] = useState<string>('all');
  const [filterModel, setFilterModel] = useState<string>('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  useEffect(() => {
    fetchLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const data = await superAdminService.getAuditLogs(params);
      setLogs(data.results || []);
      setPagesCnt(Math.ceil((data.count || 0) / params.page_size));
    } catch (error) {
      console.error('Error fetching audit logs:', error);
      handleServerError(error);
      setLogs([]);
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = (log: AuditLog) => {
    setSelectedLog(log);
    setShowDetailsDialog(true);
  };

  // Apply filters
  const applyFilters = () => {
    setParams(prev => ({
      ...prev,
      page: 0,
      user_name: searchUser || null,
      action: filterAction === 'all' ? null : filterAction,
      model_name: filterModel === 'all' ? null : filterModel,
      timestamp_after: dateFrom ? new Date(dateFrom).toISOString() : null,
      timestamp_before: dateTo ? new Date(dateTo).toISOString() : null,
    }));
  };

  // Reset filters
  const resetFilters = () => {
    setSearchUser('');
    setFilterAction('all');
    setFilterModel('all');
    setDateFrom('');
    setDateTo('');
    setParams(prev => ({
      ...prev,
      page: 0,
      user_name: null,
      action: null,
      model_name: null,
      timestamp_after: null,
      timestamp_before: null,
    }));
  };

  // Get unique actions and models for filters
  const uniqueActions = useMemo(() => {
    const actions = new Set(logs.map(log => log.action));
    return Array.from(actions).sort();
  }, [logs]);

  const uniqueModels = useMemo(() => {
    const models = new Set(logs.map(log => log.model_name));
    return Array.from(models).sort();
  }, [logs]);

  // Define columns for DataTable
  const columns: ColumnDef<AuditLog>[] = useMemo(() => [
    {
      id: 'timestamp',
      accessorKey: 'timestamp',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('timestamp')} />
      ),
      cell: ({ row }) => {
        const timestamp = row.original.timestamp;
        return (
          <div className="flex flex-col">
            <span className="text-sm">{format(timestamp_to_date(timestamp), 'dd/MM/yyyy')}</span>
            <span className="text-xs text-muted-foreground">{format(timestamp_to_date(timestamp), 'HH:mm:ss')}</span>
          </div>
        );
      },
    },
    {
      id: 'user',
      accessorKey: 'user_name',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('user')} />
      ),
      cell: ({ row }) => {
        const log = row.original;
        return (
          <div className="flex flex-col">
            <span className="font-medium">{log.user_name}</span>
            <span className="text-xs text-muted-foreground">{log.user_email}</span>
          </div>
        );
      },
    },
    {
      id: 'action',
      accessorKey: 'action',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('action')} />
      ),
      cell: ({ row }) => {
        const action = row.original.action;
        const variant =
          action === 'create' ? 'default' :
            action === 'update' ? 'secondary' :
              action === 'delete' ? 'destructive' :
                'outline';
        return <Badge variant={variant}>{action}</Badge>;
      },
    },
    {
      id: 'model',
      accessorKey: 'model_name',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('model')} />
      ),
      cell: ({ row }) => {
        return <span className="font-mono text-sm">{row.original.model_name}</span>;
      },
    },
    {
      id: 'object',
      accessorKey: 'object_repr',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('object')} />
      ),
      cell: ({ row }) => {
        const objectRepr = row.original.object_repr;
        return (
          <div className="max-w-[200px] truncate" title={objectRepr}>
            {objectRepr}
          </div>
        );
      },
    },
    {
      id: 'ip_address',
      accessorKey: 'ip_address',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('ip_address')} />
      ),
      cell: ({ row }) => {
        return <span className="font-mono text-xs">{row.original.ip_address}</span>;
      },
    },
    {
      id: 'actions',
      header: () => <div className="text-center">{t('actions')}</div>,
      cell: ({ row }) => {
        return (
          <div className="flex justify-center">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleViewDetails(row.original)}
            >
              <Eye className="h-4 w-4" />
            </Button>
          </div>
        );
      },
    },
  ], [t]);

  if (loading && logs.length === 0) {
    return (
      <div className="flex h-screen">
        <div className="flex-1 min-w-0">
          <Main>
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
                <p className="mt-2 text-muted-foreground">{t('loading')}</p>
              </div>
            </div>
          </Main>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen">
      <div className="flex-1 min-w-0">
        <Main>
          <div className="space-y-6">
            <PageHeader
              leading={
                <span className="mt-1 inline-flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
                  <ScrollText className="h-5 w-5" />
                </span>
              }
              title={t('audit_logs')}
              description={t('audit_logs_description')}
            />

            <Card>
              <CardHeader>
                <CardTitle>{t('filters')}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium">{t('search_user')}</label>
                    <Input
                      placeholder={t('search_by_user_name')}
                      value={searchUser}
                      onChange={(e) => setSearchUser(e.target.value)}
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium">{t('action')}</label>
                    <Select value={filterAction} onValueChange={setFilterAction}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">{t('all')}</SelectItem>
                        <SelectItem value="create">{t('create')}</SelectItem>
                        <SelectItem value="update">{t('update')}</SelectItem>
                        <SelectItem value="delete">{t('delete')}</SelectItem>
                        {uniqueActions.filter(a => !['create', 'update', 'delete'].includes(a)).map(action => (
                          <SelectItem key={action} value={action}>{action}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium">{t('model')}</label>
                    <Select value={filterModel} onValueChange={setFilterModel}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">{t('all')}</SelectItem>
                        {uniqueModels.map(model => (
                          <SelectItem key={model} value={model}>{model}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium">{t('date_from')}</label>
                    <Input
                      type="datetime-local"
                      value={dateFrom}
                      onChange={(e) => setDateFrom(e.target.value)}
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium">{t('date_to')}</label>
                    <Input
                      type="datetime-local"
                      value={dateTo}
                      onChange={(e) => setDateTo(e.target.value)}
                    />
                  </div>

                  <div className="flex items-end gap-2">
                    <Button onClick={applyFilters} className="flex-1">
                      {t('apply_filters')}
                    </Button>
                    <Button onClick={resetFilters} variant="outline">
                      {t('reset')}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            <DataTable
              columns={columns}
              data={logs}
              pages={pagesCnt}
              params={params}
              setParams={setParams}
              showPagination={true}
              storageKey="super-admin-audit-logs-table"
            />

            {logs.length === 0 && !loading && (
              <div className="text-center py-12">
                <p className="text-muted-foreground">{t('no_audit_logs_found')}</p>
              </div>
            )}
          </div>

          {/* Log Details Dialog */}
          <Dialog open={showDetailsDialog} onOpenChange={setShowDetailsDialog}>
            <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{t('audit_log_details')}</DialogTitle>
              </DialogHeader>
              {selectedLog && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">{t('timestamp')}</p>
                      <p className="text-sm">{format(timestamp_to_date(selectedLog.timestamp), 'dd/MM/yyyy HH:mm:ss')}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">{t('action')}</p>
                      <Badge variant={
                        selectedLog.action === 'create' ? 'default' :
                          selectedLog.action === 'update' ? 'secondary' :
                            selectedLog.action === 'delete' ? 'destructive' :
                              'outline'
                      }>
                        {selectedLog.action}
                      </Badge>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">{t('user')}</p>
                      <p className="text-sm">{selectedLog.user_name}</p>
                      <p className="text-xs text-muted-foreground">{selectedLog.user_email}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">{t('ip_address')}</p>
                      <p className="text-sm font-mono">{selectedLog.ip_address}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">{t('model')}</p>
                      <p className="text-sm font-mono">{selectedLog.model_name}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">{t('object_id')}</p>
                      <p className="text-sm font-mono">{selectedLog.object_id}</p>
                    </div>
                  </div>

                  <div>
                    <p className="text-sm font-medium text-muted-foreground mb-2">{t('object_representation')}</p>
                    <p className="text-sm bg-muted p-2 rounded">{selectedLog.object_repr}</p>
                  </div>

                  {selectedLog.changes && Object.keys(selectedLog.changes).length > 0 && (
                    <div>
                      <p className="text-sm font-medium text-muted-foreground mb-2">{t('changes')}</p>
                      <div className="bg-muted p-3 rounded">
                        <pre className="text-xs overflow-x-auto">
                          {JSON.stringify(selectedLog.changes, null, 2)}
                        </pre>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </DialogContent>
          </Dialog>
        </Main>
      </div>
    </div>
  );
}
